/* =============================================================================
 *  wallOptions — owner-configurable Wall settings (localStorage-backed).
 *
 *  First setting: which EMAIL CATEGORIES surface as cards (so the wall isn't a
 *  dumping ground for every unread). Categories are sender-derived, mirroring the
 *  mail client's custom folders (mail_custom_folders → sender→folder rules). The
 *  store is an EXCLUDE list — empty by default (everything surfaces) so behaviour
 *  is unchanged until the owner prunes; new categories surface by default.
 *
 *  Reactive across the app via a window event, so toggling in the options modal
 *  re-filters the live feed/wall without a reload. Extensible — add fields to
 *  WallOptions for future wall settings (the modal already has room).
 * ============================================================================= */

import { useCallback, useEffect, useState } from 'react';

const KEY = 'wall:options';
const EVT = 'wall:options-changed';

export interface WallOptions {
  /** Email category names the owner has hidden from the wall (exclude list). */
  hiddenEmailCategories: string[];
}
const DEFAULTS: WallOptions = { hiddenEmailCategories: [] };

const read = (): WallOptions => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    const p = JSON.parse(raw);
    return { hiddenEmailCategories: Array.isArray(p?.hiddenEmailCategories) ? p.hiddenEmailCategories : [] };
  } catch { return DEFAULTS; }
};

export function useWallOptions(): { options: WallOptions; save: (next: WallOptions) => void } {
  const [options, setOptions] = useState<WallOptions>(read);
  useEffect(() => {
    const h = () => setOptions(read());
    window.addEventListener(EVT, h);
    window.addEventListener('storage', h);
    return () => { window.removeEventListener(EVT, h); window.removeEventListener('storage', h); };
  }, []);
  const save = useCallback((next: WallOptions) => {
    try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* private mode — fine */ }
    window.dispatchEvent(new Event(EVT));
  }, []);
  return { options, save };
}

/** Derive an email's category from the sender→custom-folder rules (the same way
 *  the mail client auto-folders). Leaf folders carry senderEmails; groups don't.
 *  Unmatched senders → 'Inbox' (uncategorised). */
export const emailCategoryFor = (email: any, customFolders: any[]): string => {
  const sender = String(email?.from?.[0]?.address || email?.sender || '').toLowerCase().trim();
  if (!sender) return 'Inbox';
  const hit = (customFolders || []).find(
    (f) => f && !f.isGroup && Array.isArray(f.senderEmails) &&
      f.senderEmails.some((s: string) => String(s).toLowerCase().trim() === sender),
  );
  return hit?.name || 'Inbox';
};

/** All categories available to filter on: 'Inbox' (uncategorised) + every leaf
 *  custom folder, sorted with Inbox first. */
export const listEmailCategories = (customFolders: any[]): string[] => {
  const names = new Set<string>();
  for (const f of customFolders || []) if (f && !f.isGroup && f.name) names.add(String(f.name));
  return ['Inbox', ...Array.from(names).sort((a, b) => a.localeCompare(b))];
};
